import { useTracking } from "@/requests/hooks/app";
import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { faSpinnerThird } from "@fortawesome/pro-duotone-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ButtonHTMLAttributes, ReactNode, MouseEvent } from "react";
import { Link, type LinkProps } from "react-router-dom";
import { VariantProps, tv } from "tailwind-variants";

const button = tv({
  slots: {
    base: "group/button relative inline-flex items-center justify-center overflow-hidden rounded-full border-2 border-transparent px-30 py-10 text-center font-semibold transition-all duration-300 disabled:cursor-not-allowed",
    gradient:
      "absolute inset-0 rounded-full bg-gradient-to-b from-white/50 from-0% to-transparent to-30% bg-clip-border",
    children: "z-10 flex flex-row items-center gap-5 text-base",
    linkGradient:
      "absolute bottom-0 h-2 w-full -translate-x-full bg-gradient-to-r from-primary to-green transition-all group-hover/button:translate-x-0",
    linkUnderline:
      "absolute bottom-0 h-2 w-full bg-secondary transition-all group-hover/button:translate-x-full",
  },
  variants: {
    variant: {
      primary:
        "border-none bg-primary text-white hover:-translate-y-3 disabled:grayscale disabled:hover:translate-y-0",
      "secondary-outline":
        "border-secondary bg-transparent py-5 text-secondary hover:-translate-y-3 disabled:border-gray disabled:text-gray disabled:hover:translate-y-0",
      "white-outline":
        "border-white bg-transparent text-white hover:-translate-y-3 disabled:border-gray-dark disabled:text-gray-dark disabled:hover:translate-y-0",
      gradient:
        "border-none bg-primary bg-gradient-to-r from-primary to-green bg-clip-border text-white hover:-translate-y-3 disabled:grayscale disabled:hover:translate-y-0",
      link: "inline-flex min-w-[unset] max-w-max rounded-none border-x-0 border-t-2 border-t-transparent px-5 py-0 text-secondary",
      "link-light":
        "min-w-[unset] max-w-max rounded-none border-x-0 border-t-2 border-t-transparent px-5 font-normal text-secondary",
      "white-link": {
        base: "inline-flex min-w-[unset] max-w-max rounded-none border-x-0 border-t-2 border-t-transparent px-5 py-0 text-white",
        linkUnderline: "bg-white",
      },
      "white-background":
        "border-none bg-white text-secondary hover:-translate-y-3 disabled:grayscale disabled:hover:translate-y-0",
      success:
        "border-none bg-green text-white hover:-translate-y-3 disabled:grayscale disabled:hover:translate-y-0",
    },
    width: {
      sm: "md:min-w-[180px]",
      md: "md:min-w-[200px]",
      lg: "md:min-w-[255px]",
      full: "!min-w-full",
      unset: "min-w-[unset]",
    },
    disabled: {
      true: { base: "pointer-events-none cursor-not-allowed opacity-40" },
    },
  },
  compoundSlots: [
    {
      slots: ["linkGradient", "linkUnderline"],
      variant: "link-light",
      className: "h-1",
    },
    {
      slots: ["base"],
      variant: "link-light",
      className: "py-0",
    },
    {
      slots: ["base"],
      variant: ["primary"],
      className: "min-w-[255px]",
    },
  ],
  defaultVariants: {
    variant: "primary",
  },
});

type BaseProps = {
  children: ReactNode;
  variant?: VariantProps<typeof button>["variant"];
  width?: VariantProps<typeof button>["width"];
  loading?: boolean;
  icon?: IconProp;
  iconPosition?: "start" | "end";
  iconColor?: string;
  iconClassName?: string;
  trackingId?: IGetEventProperties | string;
  testId?: string;
  extraClassNames?: string;
  disabled?: boolean;
};

type ButtonAsButton = BaseProps &
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, keyof BaseProps> & {
    as?: "button";
  };

type ButtonAsLink = BaseProps &
  Omit<LinkProps, keyof BaseProps> & {
    as: "link";
  };

type ButtonAsExternal = BaseProps &
  Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, keyof BaseProps> & {
    as: "a";
  };

export type ButtonProps = ButtonAsButton | ButtonAsExternal | ButtonAsLink;

const Button = ({
  as,
  variant = "primary",
  width,
  children,
  onClick,
  loading = false,
  icon,
  iconPosition = "start",
  iconColor = "inherit",
  iconClassName = "h-30",
  trackingId,
  testId,
  extraClassNames,
  disabled = false,
  ...rest
}: ButtonProps): JSX.Element => {
  const {
    base,
    gradient,
    children: childrenStyles,
    linkUnderline,
    linkGradient,
  } = button({ variant, width, disabled });

  const { mutate: sendTrackingEvent } = useTracking();

  const handleOnClick = (
    event: MouseEvent<HTMLButtonElement> & MouseEvent<HTMLAnchorElement>,
  ) => {
    if (loading) return;
    if (trackingId) sendTrackingEvent(trackingId);
    if (onClick) onClick(event);
  };

  const renderIcon = (icon: IconProp) => (
    <FontAwesomeIcon
      fixedWidth
      icon={loading ? faSpinnerThird : icon}
      color={iconColor}
      spin={loading}
      className={iconClassName}
    />
  );

  const renderGradientOverlay = () =>
    ["primary", "gradient", "success"].includes(variant) && (
      <span className={gradient()}></span>
    );

  const renderIconsWithChildren = () => (
    <>
      {!!icon && iconPosition === "start" && renderIcon(icon)}
      {!icon && loading ? renderIcon(faSpinnerThird) : children}
      {!!icon && iconPosition === "end" && renderIcon(icon)}
    </>
  );

  const renderAnimatedUnderline = () =>
    ["link", "link-light", "white-link"].includes(variant) && (
      <>
        <span className={linkGradient()}></span>
        <span className={linkUnderline()}></span>
      </>
    );

  const renderChildren = () => (
    <>
      {renderGradientOverlay()}
      <span className={childrenStyles()}>{renderIconsWithChildren()}</span>
      {renderAnimatedUnderline()}
    </>
  );

  if (as === "link")
    return (
      <Link data-testid={testId} className={base()} {...(rest as ButtonAsLink)}>
        {renderChildren()}
      </Link>
    );

  if (as === "a")
    return (
      <a
        className={base({ className: extraClassNames })}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={testId}
        {...(rest as ButtonAsExternal)}>
        {renderChildren()}
      </a>
    );

  return (
    <button
      className={base({ className: extraClassNames })}
      onClick={handleOnClick}
      data-testid={testId}
      disabled={disabled}
      {...(rest as ButtonAsButton)}>
      {renderChildren()}
    </button>
  );
};

export default Button;
